import { useNavigate } from 'react-router-dom';
import { Wrench, ClipboardCheck, PlusCircle, MessageSquare, Upload, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import StatCard from '../../components/ui/StatCard';
import Badge from '../../components/ui/Badge';
import { useAgentDashboard } from '../../hooks/useDashboard';
import { RecentActivityFeed } from '../../components/ui/RecentActivityFeed';
import { DashboardSkeleton } from '../../components/design-system/EmptyState';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, isError, error, refetch } = useAgentDashboard();

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="flex items-center gap-2 text-coral-600">
          <AlertCircle size={20} />
          <span className="text-sm font-medium">{(error as Error)?.message || 'Failed to load dashboard data. Please try again.'}</span>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-10 text-sm font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const newMaint = data?.newMaintenanceRequests ?? 0;
  const pendMaint = data?.pendingMaintenanceRequests ?? 0;

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900 leading-tight">Agent Dashboard</h1>
          <p className="text-[13px] text-ink-500 mt-0.5">Welcome back, {user?.firstName || user?.name || 'there'}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Managed Properties"    value={data?.managedProperties ?? 0} />
        <StatCard label="Active Leases"          value={data?.activeLeases ?? 0}      tone="teal" />
        <StatCard label="New Maintenance"        value={newMaint}                     tone={newMaint ? 'danger' : 'default'} />
        <StatCard label="Pending Maintenance"    value={pendMaint}                    tone={pendMaint ? 'warn' : 'default'} />
      </div>

      {/* Quick actions */}
      <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
        <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {([
            { icon: <Wrench size={18} />,         label: 'Maintenance', to: '/agent/maintenance' },
            { icon: <ClipboardCheck size={18} />, label: 'Inspections', to: '/agent/inspections/create' },
            { icon: <PlusCircle size={18} />,     label: 'Create Lease', to: '/agent/leases/new' },
            { icon: <Upload size={18} />,          label: 'Upload Doc',   to: '/documents/upload' },
            { icon: <MessageSquare size={18} />,   label: 'Messages',    to: '/messages' },
          ] as const).map(a => (
            <button
              key={a.to}
              onClick={() => navigate(a.to)}
              className="flex flex-col items-center gap-2 p-4 rounded-10 border border-ink-200 text-[13px] font-medium text-ink-800 hover:border-forest-900 hover:bg-forest-50 transition-colors"
            >
              <span className="text-forest-900">{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Priorities + Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider">Today's Priorities</h2>
            {newMaint > 0 && <Badge tone="danger" dot>{newMaint} urgent</Badge>}
          </div>
          <div className="space-y-2">
            {newMaint > 0 ? (
              <button
                onClick={() => navigate('/agent/maintenance')}
                className="flex items-center gap-3 w-full p-3 rounded-8 bg-danger-50 border border-red-200 text-left hover:bg-red-100 transition-colors"
              >
                <Wrench size={15} className="text-danger-700 flex-none" />
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-ink-900">New maintenance requests</p>
                  <p className="text-[11px] text-ink-600">{newMaint} need attention</p>
                </div>
                <Badge tone="danger">{newMaint}</Badge>
              </button>
            ) : (
              <p className="text-[13px] text-ink-500 py-4 text-center">No urgent items today</p>
            )}
            {(data?.inspectionsDue ?? 0) > 0 && (
              <button
                onClick={() => navigate('/agent/inspections')}
                className="flex items-center gap-3 w-full p-3 rounded-8 bg-warn-50 border border-yellow-200 text-left hover:bg-yellow-100 transition-colors"
              >
                <ClipboardCheck size={15} className="text-warn-700 flex-none" />
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-ink-900">Inspections due</p>
                  <p className="text-[11px] text-ink-600">{data?.inspectionsDue} scheduled</p>
                </div>
                <Badge tone="warn">{data?.inspectionsDue}</Badge>
              </button>
            )}
          </div>
        </div>

        <RecentActivityFeed limit={5} />
      </div>
    </div>
  );
};

export default AgentDashboard;
